import React from "react";
import { useNavigate } from "react-router-dom";
import { Snackbar, CircularProgress } from "@mui/material";
import CashModal from "./components/modals/CashModal";
import CreditModal from "./components/modals/CreditModal";
import OrderSummary from "./components/orderSummary/CarOrderSummary";
import { roundValue } from "./utils/helpers";
import CarOrderPrint from "./components/PrinterContent";
import { useBusinessInfo } from "./hooks/useBusinessInfo";
import { usePayment } from "./hooks/usePayment";
import { useOrder } from "./hooks/useOrder";
import OrderProductList from "./components/OrderProductList";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import CommonButtons from "src/shared/components/CommonButtons";
import { toast, ToastContainer } from "react-toastify";
import { PageTitle } from "src/shared/PageTitle";

const CarOrderDetails: React.FC = () => {
  const navigate = useNavigate();
  const business = useBusinessInfo();
  const orderHook = useOrder();
  const paymentHook = usePayment(orderHook.order, orderHook.setOrder, orderHook.setStatus, orderHook.order?.Orderid?.toString());

  const { order, status, loading, error, setError } = orderHook;
  const { paymentMethod, openCashModal, openCreditModal, receivedPrice, setModals, setReceivedPrice, handleConfirmPayment, handlePaymentMethodChange, handleCloseModal, showPrintContent } =
    paymentHook;

  const handlePrint = () => {
    setModals((prev) => ({ ...prev, print: true }));
    setTimeout(() => {
      window.print();
      setModals((prev) => ({ ...prev, print: false }));

      toast.success("Order printed successfully!");

      setTimeout(() => {
        // navigate("/v1/new-order");
      }, 2000);
    }, 0);
  };

  const cashModalProps = { order, paymentMethod, receivedPrice, setReceivedPrice, handleCloseModal, handleConfirmPayment };
  const creditModalProps = { order, paymentMethod, handleCloseModal, handleConfirmPayment };
  const summaryProps = {
    order,
    status,
    setStatus: orderHook.setStatus,
    handleStatusChange: orderHook.handleStatusChange,
    paymentMethod,
    handlePaymentMethodChange,
    roundValue,
    cancelOrder: orderHook.cancelOrder,
    isCancelDisabled: orderHook.isCancelDisabled,
    handlePrint,
  };

  return (
    <div className="h-[88vh] bg-gray-50 p-5 overflow-hidden">
       <PageTitle title="Order-Details" />
      <div className="h-full flex flex-col">
        {/* Compact Header */}
        <div className="mb-3 flex items-center justify-between flex-shrink-0">
          <CommonButtons label="BACK" onClick={() => navigate(-1)} variant="secondary" icon={KeyboardReturnIcon} className="text-sm" />
          <h1 className="text-xl font-bold text-gray-800">Order Details</h1>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-full">
            <CircularProgress size={24} />
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-[60%_40%] gap-3 min-h-0">
            {/* Product List - 60% width */}
            <div className="bg-white rounded border border-gray-200 overflow-hidden h-full">
              <OrderProductList order={order} />
            </div>

            {/* Order Summary - 40% width */}
            <div className="bg-white rounded border border-gray-200 overflow-hidden h-full">
              <OrderSummary {...summaryProps} />
            </div>
          </div>
        )}

        {/* Modals and Toast */}
        {openCashModal && <CashModal {...cashModalProps} />}
        {openCreditModal && <CreditModal {...creditModalProps} />}
        {error && <Snackbar open autoHideDuration={6000} onClose={() => setError(null)} message={error} />}

        {showPrintContent && order && (
          <div className="print-section">
            <CarOrderPrint {...business} order={order} latestPayment={paymentHook.latestPayment} />
          </div>
        )}

        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      </div>
    </div>
  );
};

export default CarOrderDetails;
